import { useEffect, useMemo, useRef, useState } from "react";
import ProgressBar from "./ui/ProgressBar.jsx";

const ORDER = ["foundation", "identity", "authentication", "applications", "groups", "compliance"];

const LABEL = {
  foundation: "FOUNDATION",
  identity: "IDENTITY",
  authentication: "AUTHENTICATION",
  applications: "APPLICATIONS",
  groups: "GROUPS & LIFECYCLE",
  lifecycle: "GROUPS & LIFECYCLE",
  compliance: "COMPLIANCE",
};

function normalizeCategory(raw) {
  const s = String(raw || "foundation").toLowerCase();
  if (s.includes("group") || s.includes("lifecycle")) return "groups";
  if (ORDER.includes(s)) return s;
  return "foundation";
}

function groupItems(items) {
  const buckets = new Map();
  for (const key of ORDER) {
    buckets.set(key, []);
  }
  for (const it of items) {
    if (!it || typeof it !== "object") continue;
    const cat = normalizeCategory(it.category);
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat).push(it);
  }
  return buckets;
}

function itemComplete(it) {
  if (!it || typeof it !== "object") return false;
  const st = String(it.status || "").toLowerCase();
  if (st === "complete" || st === "completed") return true;
  if (it.completed === true || it.done === true) return true;
  return false;
}

function StatusGlyph({ status, checkPop }) {
  switch (String(status || "").toLowerCase().replace(/\s+/g, "_")) {
    case "complete":
    case "completed":
      return (
        <span
          className={checkPop ? "idwiz-roadmap-check-pop" : undefined}
          style={{ color: "var(--healthy)", fontSize: 14, lineHeight: 1 }}
        >
          ✓
        </span>
      );
    case "in_progress":
      return (
        <span
          style={{
            display: "inline-block",
            width: 12,
            height: 12,
            border: "2px solid var(--accent)",
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            flexShrink: 0,
          }}
        />
      );
    case "blocked":
      return <span style={{ color: "var(--text-dim)", fontSize: 12 }}>⏳</span>;
    case "available":
    default:
      return <span style={{ color: "var(--accent)", fontSize: 14 }}>⚡</span>;
  }
}

export default function Roadmap({ items = [], onSelectItem, selectedId = null, onMarkComplete = null }) {
  const list = Array.isArray(items) ? items : [];
  const dedupedItems = useMemo(() => {
    const seen = new Set();
    return list.filter((item) => {
      const key = String(item?.title ?? "")
        .toLowerCase()
        .trim();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [list]);

  const [checkPopIds, setCheckPopIds] = useState(() => new Set());
  const prevDoneRef = useRef(new Map());

  useEffect(() => {
    for (const it of dedupedItems) {
      const id = String(it.id ?? it.title ?? "");
      if (!id) continue;
      const done = itemComplete(it);
      const was = prevDoneRef.current.get(id) === true;
      if (done && !was) {
        setCheckPopIds((prev) => new Set(prev).add(id));
        window.setTimeout(() => {
          setCheckPopIds((prev) => {
            const s = new Set(prev);
            s.delete(id);
            return s;
          });
        }, 900);
      }
      prevDoneRef.current.set(id, done);
    }
  }, [dedupedItems]);

  const total = dedupedItems.length;
  const complete = dedupedItems.filter((it) => itemComplete(it)).length;
  const pct = total > 0 ? (complete / total) * 100 : 0;

  if (dedupedItems.length === 0) {
    return (
      <aside
        style={{
          width: "240px",
          flexShrink: 0,
          borderRight: "1px solid var(--border)",
          background: "var(--bg-surface)",
          minHeight: "100%",
          padding: "16px 12px",
        }}
      >
        <p className="text-dim" style={{ fontSize: "12px", letterSpacing: "0.1em", marginBottom: "8px" }}>
          ROADMAP
        </p>
        <p className="text-dim" style={{ fontSize: "13px" }}>
          Roadmap generates after discovery
        </p>
      </aside>
    );
  }

  const buckets = groupItems(dedupedItems);

  return (
    <aside
      style={{
        width: "240px",
        flexShrink: 0,
        borderRight: "1px solid var(--border)",
        background: "var(--bg-surface)",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 12px 12px", flexShrink: 0 }}>
        <p className="text-dim" style={{ fontSize: "11px", letterSpacing: "0.1em", marginBottom: "6px" }}>
          ROADMAP
        </p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span className="text-accent" style={{ fontSize: "13px", fontWeight: 600 }}>
            {complete}/{total}
          </span>
        </div>
        <div style={{ marginTop: "8px" }}>
          <ProgressBar value={pct} showPercent={false} />
        </div>
      </div>
      <nav style={{ overflow: "auto", flex: 1, padding: "0 8px 16px" }}>
        {ORDER.map((key) => {
          const group = buckets.get(key) || [];
          if (group.length === 0) return null;
          const header = LABEL[key] || key.toUpperCase();
          const categoryAllDone = group.length > 0 && group.every((it) => itemComplete(it));
          return (
            <div key={key}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: "16px",
                  marginBottom: "8px",
                  paddingLeft: "4px",
                }}
              >
                <p
                  className="text-dim"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    margin: 0,
                  }}
                >
                  {header}
                </p>
                {categoryAllDone ? (
                  <span className="text-accent" style={{ fontSize: "11px", fontWeight: 700 }}>
                    ✓
                  </span>
                ) : null}
              </div>
              <ul style={{ listStyle: "none" }}>
                {group.map((it) => {
                  const id = it.id != null ? String(it.id) : "";
                  const st = String(it.status || "available").toLowerCase();
                  const isSel = selectedId && id === String(selectedId);
                  const isAvailable = st === "available";
                  const isComplete = itemComplete(it);
                  const isBlocked = st === "blocked";
                  const isInProgress = st === "in_progress" || st === "in progress";
                  const rowKey = id || String(it.title);
                  const checkPop = checkPopIds.has(rowKey);

                  let titleClass = "roadmap-strike-title";
                  let titleStyle = { fontSize: "13px" };
                  if (isComplete) {
                    titleClass += " roadmap-strike-title-done";
                    titleStyle = { ...titleStyle, color: "var(--text-dim)" };
                  } else if (isBlocked) {
                    titleStyle = { ...titleStyle, color: "var(--text-dim)" };
                  } else if (isInProgress) {
                    titleStyle = { ...titleStyle, color: "var(--text-primary)", fontWeight: 500 };
                  } else if (isAvailable) {
                    titleStyle = { ...titleStyle, color: "var(--text-primary)" };
                  }

                  return (
                    <li
                      key={rowKey}
                      className="roadmap-item"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 4,
                        marginBottom: "2px",
                      }}
                      onMouseEnter={(e) => {
                        const btn = e.currentTarget.querySelector(".idwiz-mark-complete");
                        if (btn?.style) btn.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        const btn = e.currentTarget.querySelector(".idwiz-mark-complete");
                        if (btn?.style) btn.style.opacity = "0";
                      }}
                    >
                      <button
                        type="button"
                        disabled={!isAvailable}
                        onClick={() => isAvailable && onSelectItem?.(it)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: "8px 8px",
                          border: "none",
                          borderRadius: "4px",
                          borderLeft: isSel ? "3px solid var(--accent)" : "3px solid transparent",
                          background: isAvailable ? "transparent" : "transparent",
                          cursor: isAvailable ? "pointer" : "default",
                          textAlign: "left",
                          transition: "background 150ms ease",
                        }}
                        onMouseEnter={(e) => {
                          if (isAvailable) e.currentTarget.style.background = "var(--bg-elevated)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <StatusGlyph status={it.status} checkPop={checkPop && isComplete} />
                        <span className={isComplete ? titleClass : undefined} style={{ flex: 1, ...titleStyle }}>
                          {it.title || "Untitled"}
                        </span>
                        {isAvailable ? (
                          <span className="text-dim" style={{ fontSize: "12px" }}>
                            →
                          </span>
                        ) : null}
                      </button>
                      {it.status !== "complete" && typeof onMarkComplete === "function" && it.id != null ? (
                        <button
                          type="button"
                          className="idwiz-mark-complete"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMarkComplete(it.id);
                          }}
                          title="Mark as complete"
                          style={{
                            background: "none",
                            border: "1px solid var(--border)",
                            borderRadius: 3,
                            color: "var(--text-dim)",
                            cursor: "pointer",
                            fontSize: 9,
                            padding: "1px 5px",
                            marginLeft: 6,
                            flexShrink: 0,
                            opacity: 0,
                            transition: "opacity 150ms",
                          }}
                        >
                          ✓ done
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
